import mixpanel from 'mixpanel-browser'

export enum AnalyticsEvent {
  // Case Management
  SIGNED_IN_CASE_CREATED = 'Signed In Case Created',
  SIGNED_IN_CASE_CREATION_FAILED = 'Signed In Case Creation Failed',
  PUBLIC_CASE_CREATED = 'Public Case Created',
  PUBLIC_CASE_CREATION_FAILED = 'Public Case Creation Failed',

  CASE_FILTER_APPLIED = 'Case Filter Applied',

  // Invoice Management
  INVOICE_PAID = 'Invoice Paid',
  INVOICE_PAYMENT_FAILED = 'Invoice Payment Failed',
  INVOICE_DOWNLOADED = 'Invoice Downloaded',
  INVOICE_DETAIL_VIEWED = 'Invoice Detail Viewed',
  INVOICE_FILTER_APPLIED = 'Invoice Filter Applied',

  // User Authentication
  USER_LOGIN = 'User Login',
  USER_LOGOUT = 'User Logout',

  // Page Views
  PAGE_VIEW = 'Page View',
  PAGE_LOAD_TIME = 'Page Load Time',

  // Site Management
  SITE_DETAIL_VIEWED = 'Site Detail Viewed',

  // Sensor Management
  SENSOR_DETAIL_VIEWED = 'Sensor Detail Viewed',
  SENSOR_FILTER_APPLIED = 'Sensor Filter Applied',

  // Reports
  POWERBI_REPORT_VIEWED = 'Power BI Report Viewed',
  SSRS_REPORT_VIEWED = 'SSRS Report Viewed',

  // User Preferences
  LANGUAGE_CHANGED = 'Language Changed',
  THEME_CHANGED = 'Theme Changed',
  NOTIFICATION_TOGGLE = 'Notification Setting Changed',

  // API Performance
  API_SUCCESS = 'API Success',
  API_FAILURE = 'API Failure',
}

import {
  ApiEventProperties,
  FilterAppliedProperties,
  InvoiceDetailViewedProperties,
  InvoiceDownloadedProperties,
  InvoicePaidProperties,
  InvoicePaymentFailedProperties,
  LanguageChangedProperties,
  NotificationToggleProperties,
  PageLoadTimeProperties,
  PageViewProperties,
  PublicCaseCreatedProperties,
  PublicCaseCreationFailedProperties,
  ReportViewedProperties,
  SensorDetailViewedProperties,
  SignedInCaseCreatedProperties,
  SignedInCaseCreationFailedProperties,
  SiteDetailViewedProperties,
  ThemeChangedProperties,
} from '../types/analytics'

class AnalyticsService {
  private isInitialized = false

  public setInitialized(): void {
    this.isInitialized = true
  }

  private isMixpanelInitialized(): boolean {
    return (
      this.isInitialized && !!(mixpanel && typeof mixpanel.track === 'function')
    )
  }

  // Track case creation
  trackSignedInCaseCreated(properties: SignedInCaseCreatedProperties): void {
    this.trackEvent(AnalyticsEvent.SIGNED_IN_CASE_CREATED, properties)
  }

  // Track case creation failure
  trackSignedInCaseCreationFailed(
    properties: SignedInCaseCreationFailedProperties
  ): void {
    this.trackEvent(AnalyticsEvent.SIGNED_IN_CASE_CREATION_FAILED, properties)
  }

  // Track public case creation
  trackPublicCaseCreated(properties: PublicCaseCreatedProperties): void {
    this.trackEvent(AnalyticsEvent.PUBLIC_CASE_CREATED, properties)
  }

  // Track public case creation failure
  trackPublicCaseCreationFailed(
    properties: PublicCaseCreationFailedProperties
  ): void {
    this.trackEvent(AnalyticsEvent.PUBLIC_CASE_CREATION_FAILED, properties)
  }

  // Track invoice payment
  trackInvoicePaid(properties: InvoicePaidProperties): void {
    this.trackEvent(AnalyticsEvent.INVOICE_PAID, properties)

    // Also increment Mixpanel people property
    if (this.isMixpanelInitialized()) {
      mixpanel.people.increment('Total Invoices Paid')
    }
  }

  // Track invoice payment failure
  trackInvoicePaymentFailed(properties: InvoicePaymentFailedProperties): void {
    this.trackEvent(AnalyticsEvent.INVOICE_PAYMENT_FAILED, properties)
  }

  // Track invoice download
  trackInvoiceDownloaded(properties: InvoiceDownloadedProperties): void {
    this.trackEvent(AnalyticsEvent.INVOICE_DOWNLOADED, properties)

    // Increment download counter
    if (this.isMixpanelInitialized()) {
      mixpanel.people.increment('Total Invoice Downloads')
    }
  }

  // Track invoice detail view
  trackInvoiceDetailViewed(properties: InvoiceDetailViewedProperties): void {
    this.trackEvent(AnalyticsEvent.INVOICE_DETAIL_VIEWED, properties)
  }

  // Track filter application (invoice, case, sensor)
  trackFilterApplied(properties: FilterAppliedProperties): void {
    const eventName =
      properties.filterType === 'case'
        ? AnalyticsEvent.CASE_FILTER_APPLIED
        : properties.filterType === 'invoice'
          ? AnalyticsEvent.INVOICE_FILTER_APPLIED
          : AnalyticsEvent.SENSOR_FILTER_APPLIED

    this.trackEvent(eventName, properties)
  }

  // Track user login
  trackUserLogin(userId: string, email?: string): void {
    if (this.isMixpanelInitialized()) {
      mixpanel.identify(userId)

      if (email) {
        mixpanel.people.set({
          $email: email.toLowerCase(),
        })
      }
    }

    this.trackEvent(AnalyticsEvent.USER_LOGIN, {
      userId,
      email: email?.toLowerCase(),
    })

    // Increment login count
    if (this.isMixpanelInitialized()) {
      mixpanel.people.increment('Total Logins')
    }
  }

  // Track user logout
  trackUserLogout(): void {
    this.trackEvent(AnalyticsEvent.USER_LOGOUT, {})
  }

  // Track page view
  trackPageView(properties: PageViewProperties): void {
    this.trackEvent(AnalyticsEvent.PAGE_VIEW, properties)
  }

  // Track page load time
  trackPageLoadTime(properties: PageLoadTimeProperties): void {
    this.trackEvent(AnalyticsEvent.PAGE_LOAD_TIME, properties)
  }

  // Track site detail view
  trackSiteDetailViewed(properties: SiteDetailViewedProperties): void {
    this.trackEvent(AnalyticsEvent.SITE_DETAIL_VIEWED, properties)

    // Increment site view counter
    if (this.isMixpanelInitialized()) {
      mixpanel.people.increment('Total Site Views')
    }
  }

  // Track sensor detail view
  trackSensorDetailViewed(properties: SensorDetailViewedProperties): void {
    this.trackEvent(AnalyticsEvent.SENSOR_DETAIL_VIEWED, properties)

    // Increment sensor view counter
    if (this.isMixpanelInitialized()) {
      mixpanel.people.increment('Total Sensor Views')
    }
  }

  // Track Power BI report view
  trackPowerBIReportViewed(properties: ReportViewedProperties): void {
    this.trackEvent(AnalyticsEvent.POWERBI_REPORT_VIEWED, {
      ...properties,
      reportType: 'powerbi',
    })

    // Increment report view counter
    if (this.isMixpanelInitialized()) {
      mixpanel.people.increment('Total Power BI Report Views')
      mixpanel.people.increment(`Power BI: ${properties.reportName}`)
    }
  }

  // Track SSRS report view
  trackSSRSReportViewed(properties: ReportViewedProperties): void {
    this.trackEvent(AnalyticsEvent.SSRS_REPORT_VIEWED, {
      ...properties,
      reportType: 'ssrs',
    })

    // Increment report view counter
    if (this.isMixpanelInitialized()) {
      mixpanel.people.increment('Total SSRS Report Views')
      mixpanel.people.increment(`SSRS: ${properties.reportName}`)
    }
  }

  // Track language change
  trackLanguageChanged(properties: LanguageChangedProperties): void {
    this.trackEvent(AnalyticsEvent.LANGUAGE_CHANGED, properties)

    // Update user profile
    if (this.isMixpanelInitialized()) {
      mixpanel.people.set({
        'Preferred Language': properties.toLanguage,
      })
    }
  }

  // Track theme change
  trackThemeChanged(properties: ThemeChangedProperties): void {
    this.trackEvent(AnalyticsEvent.THEME_CHANGED, properties)

    // Update user profile
    if (this.isMixpanelInitialized()) {
      mixpanel.people.set({
        'Preferred Theme': properties.toTheme,
      })
    }
  }

  // Track notification setting toggle
  trackNotificationToggle(properties: NotificationToggleProperties): void {
    this.trackEvent(AnalyticsEvent.NOTIFICATION_TOGGLE, properties)

    // Update user profile with notification preferences
    if (this.isMixpanelInitialized()) {
      const settingKey = `Notification: ${properties.notificationType} - ${properties.channel}`
      mixpanel.people.set({
        [settingKey]: properties.enabled,
      })
    }
  }

  // Track API success
  trackApiSuccess(properties: ApiEventProperties): void {
    this.trackEvent(AnalyticsEvent.API_SUCCESS, properties)
  }

  // Track API failure
  trackApiFailure(properties: ApiEventProperties): void {
    this.trackEvent(AnalyticsEvent.API_FAILURE, properties)
  }

  // Generic track event method
  trackEvent(
    eventName: string,
    properties?: Record<string, unknown> | unknown
  ): void {
    // Don't wrap in try-catch at the top level - let individual checks handle errors
    if (!this.isMixpanelInitialized()) {
      // Debug: Check what's wrong with initialization
      let tokenInfo = 'no get_config'
      try {
        if (typeof mixpanel?.get_config === 'function') {
          const config = mixpanel.get_config()
          tokenInfo = config?.token || 'no token in config'
        }
      } catch {
        tokenInfo = 'get_config error'
      }

      console.warn('Mixpanel not initialized yet, skipping event:', eventName, {
        mixpanelExists: !!mixpanel,
        hasTrack: typeof mixpanel?.track === 'function',
        hasGetConfig: typeof mixpanel?.get_config === 'function',
        token: tokenInfo,
      })
      return
    }

    try {
      mixpanel.track(eventName, {
        ...(properties as Record<string, unknown>),
        timestamp: new Date().toISOString(),
      })
    } catch (error) {
      console.error('Error tracking event:', eventName, error)
    }
  }

  // Time an event (useful for measuring durations)
  timeEvent(eventName: string): void {
    try {
      if (this.isMixpanelInitialized()) {
        mixpanel.time_event(eventName)
      }
    } catch (error) {
      console.error('Error timing event:', eventName, error)
    }
  }

  // Set user properties
  setUserProperties(properties: Record<string, unknown>): void {
    try {
      if (this.isMixpanelInitialized()) {
        mixpanel.people.set(properties)
      }
    } catch (error) {
      console.error('Error setting user properties:', error)
    }
  }

  // Increment a user property
  incrementUserProperty(property: string, value: number = 1): void {
    try {
      if (this.isMixpanelInitialized()) {
        mixpanel.people.increment(property, value)
      }
    } catch (error) {
      console.error('Error incrementing user property:', property, error)
    }
  }
}

export const analytics = new AnalyticsService()

// HELPER FUNCTIONS

// Get payment source from URL
export function getPaymentSource():
  | 'pdf_link'
  | 'email_link'
  | 'portal_pay_button' {
  const url = new URL(window.location.href)
  const isGuestPayment = url.pathname.includes('/guest-invoice-pmt')

  if (!isGuestPayment) {
    return 'portal_pay_button'
  }

  const referrer = document.referrer
  if (referrer.includes('pdf') || url.searchParams.get('source') === 'pdf') {
    return 'pdf_link'
  }

  return 'email_link'
}

// Get page load performance metrics

export function getPageLoadMetrics(
  pageName: string
): PageLoadTimeProperties | null {
  try {
    const navigation = performance.getEntriesByType(
      'navigation'
    )[0] as PerformanceNavigationTiming

    if (!navigation) return null

    return {
      pageName,
      pageUrl: window.location.href,
      loadTime: navigation.loadEventEnd - navigation.fetchStart,
      dnsTime: navigation.domainLookupEnd - navigation.domainLookupStart,
      tcpTime: navigation.connectEnd - navigation.connectStart,
      requestTime: navigation.responseStart - navigation.requestStart,
      responseTime: navigation.responseEnd - navigation.responseStart,
    }
  } catch (error) {
    console.error('Error getting page load metrics:', error)
    return null
  }
}
